import { useState } from 'react'
import { DUREES_FORCAGE, type Resume } from '../../api/meteo'
import { arreterMeteo, forcerMeteo } from '../../api/meteoAcces'

/**
 * Les deux décisions du dispatch en mode automatique :
 *  • ARRÊTER — « il ne pleut plus » : Normale, et l'automate attend un NOUVEL épisode ;
 *  • FORCER — pluie ou forte chaleur, pour une durée ou jusqu'à arrêt.
 * Le serveur vérifie le droit (gerer_courses ou gerer_config) et journalise.
 */
export function PilotageMeteo({
  resume,
  surFait,
  compact = false,
}: {
  resume: Resume
  surFait: () => Promise<void> | void
  compact?: boolean
}) {
  const [ouvert, setOuvert] = useState(false)
  const [condition, setCondition] = useState<'rain' | 'extreme_heat'>('rain')
  const [minutes, setMinutes] = useState<number | null>(60)
  const [enCours, setEnCours] = useState(false)
  const [message, setMessage] = useState<{ texte: string; erreur: boolean } | null>(null)

  async function agir(f: () => Promise<string | void>) {
    setEnCours(true)
    setMessage(null)
    try {
      const r = await f()
      if (r) setMessage({ texte: r, erreur: false })
      setOuvert(false)
      await surFait()
    } catch (e) {
      setMessage({ texte: e instanceof Error ? e.message : 'Opération refusée.', erreur: true })
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div className={`meteo-pilotage${compact ? ' compact' : ''}`}>
      {resume.arretable && (
        <button
          className="btn btn-outline btn-sm"
          disabled={enCours}
          title="Il ne pleut plus (ou la chaleur est retombée) : retour à Normale. L'automate attendra un nouvel épisode."
          onClick={() =>
            void agir(async () =>
              (await arreterMeteo()) === 'rien_a_arreter' ? 'Rien à arrêter : la météo est déjà Normale.' : undefined,
            )
          }
        >
          Arrêter
        </button>
      )}
      <button className="btn btn-outline btn-sm" disabled={enCours} aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}>
        Forcer…
      </button>
      {ouvert && (
        <div className="meteo-forcer" role="group" aria-label="Forcer une condition météo">
          <div className="seg seg-sm" role="radiogroup" aria-label="Condition">
            {(['rain', 'extreme_heat'] as const).map((c) => (
              <button key={c} type="button" role="radio" aria-checked={condition === c} className={condition === c ? 'on' : ''} onClick={() => setCondition(c)}>
                {c === 'rain' ? 'Pluie' : 'Forte chaleur'}
              </button>
            ))}
          </div>
          <div className="seg seg-sm" role="radiogroup" aria-label="Durée">
            {DUREES_FORCAGE.map((d) => (
              <button
                key={d.libelle}
                type="button"
                role="radio"
                aria-checked={minutes === d.minutes}
                className={minutes === d.minutes ? 'on' : ''}
                onClick={() => setMinutes(d.minutes)}
              >
                {d.libelle}
              </button>
            ))}
          </div>
          <button className="btn btn-accent btn-sm" disabled={enCours} onClick={() => void agir(() => forcerMeteo(condition, minutes))}>
            Appliquer
          </button>
        </div>
      )}
      {message && (
        <span className="meteo-msg" style={{ color: message.erreur ? 'var(--danger)' : 'var(--n700)' }}>
          {message.texte}
        </span>
      )}
    </div>
  )
}
